import React, { Component, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import debounce from "lodash/debounce";
import get from "lodash/get";
import noop from "lodash/noop";
import sumBy from "lodash/sumBy";

import { DayGroupShape, EntriesShape, HistoryContext } from "../../props";

import TimeEntry from "./TimeEntry";

require("./DayEntry.scss");

class DayEntry extends Component {
  constructor(props) {
    super(props);

    this.state = DayEntry.getDerivedStateFromProps(props, {});
  }

  static getDerivedStateFromProps(props, state) {
    const result = {
      isEditing: false,
      isCloning: false,
      allSubmitted: true,
      totalHours: 0
    };

    const newState = {};

    const { editingEntry, entries } = props;

    for (const entry of props.entries) {
      if (entry.id === props.editingEntry.id) {
        result.isEditing = true;
      }

      if (entry.id === props.editingEntry._clonedFrom) {
        result.isCloning = true;
      }

      result.allSubmitted = result.allSubmitted && Boolean(entry.submitDate);
      result.totalHours += entry.hours;
    }

    result.isEditingOrCloning = result.isEditing || result.isCloning;

    let applyState = false;

    for (const [key, value] of Object.entries(result)) {
      if (get(state, key) !== value) {
        applyState = true;

        newState[key] = value;
      }
    }

    return applyState ? newState : null;
  }

  _renderSummary() {
    if (this.props.isWorkDay) {
      const hoursClasses = classNames("worked-hours", {
        highlight: this.state.totalHours < this.props.workDay.workingHours
      });

      return (
        <div className="summary">
          <span className={hoursClasses}>
            {this.state.totalHours}
          </span>
          {" / "}
          <span className="working-hours">
            {this.props.workDay.workingHours}
          </span> hours
        </div>
      );
    }

    return null;
  }

  _renderEntries() {
    return this.props.entries.map((entry, i) => {
      const onDelete = () => {
        return this.props.onDelete(entry);
      };

      const onSubmit = () => {
        return this.props.onSubmit(entry);
      };

      return (
        <TimeEntry
          key={`${i}-${entry.id}`}
          entry={entry}
          historyContext={this.props.historyContext}
          workDay={this.props.workDay}
          selected={this.props.selected[entry.id]}
          editingEntry={this.props.editingEntry}
          setSubmitGroupEnabled={this.props.setSubmitGroupEnabled}
          onEdit={this.props.onEdit}
          onCreate={this.props.onCreate}
          onClone={this.props.onClone}
          onDelete={onDelete}
          onSubmit={onSubmit}
        />
      );
    })
  }

  _renderCallToAction() {
    const delta = this.props.workDay.workingHours - this.state.totalHours;

    if (this.props.historyContext === "todo" && delta > 0) {
      return (
        <div className="entry">
          <div className="call-to-action col-xs-10">
            <span className="highlight">{"\u26a0 "}</span>
            {delta} more hours needed.
          </div>
        </div>
      );
    }

    return null;
  }

  render() {
    const rowClasses = classNames("row", "day", "no-margin", {
      editingOrCloning: this.state.isEditingOrCloning,
      submitted: this.state.allSubmitted,
      saved: !this.state.allSubmitted,
    });

    return (
      <div className={rowClasses} ref={this.props.measureRef}>
        <div className="col-xs-3">
          <HistoryDate firstEntryDate={this.props.firstEntryDate} />
          {this._renderSummary()}
        </div>
        <div className="time-entries col-xs-9">
          {this._renderCallToAction()}
          {this._renderEntries()}
        </div>
      </div>
    );
  }
}

DayEntry.displayName = "History.DayEntry";

DayEntry.propTypes = {
  day: DayGroupShape.isRequired,
  editingEntry: PropTypes.object.isRequired,
  entries: EntriesShape.isRequired,
  firstEntryDate: PropTypes.object.isRequired,
  hasMore: PropTypes.bool.isRequired,
  historyContext: HistoryContext,
  isWorkDay: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  measureRef: PropTypes.any,
  onClone: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selected: PropTypes.object.isRequired,
  setSubmitGroupEnabled: PropTypes.func.isRequired,
  trackVisibility: PropTypes.bool.isRequired,
  workDay: PropTypes.object.isRequired,
};

DayEntry.defaultProps = {
  hasMore: false,
  isWorkDay: false,
  isVisible: false,
  loading: false,
  onClone: noop,
  onCreate: noop,
  onDelete: noop,
  onEdit: noop,
  onSubmit: noop,
  setSubmitGroupEnabled: noop,
  trackVisibility: false,
};

function mapStateToProps(state, ownProps) {
  const historyContext = state.historyFilters.context;
  const loading = state.entry.deleteProcessing || state.entry.submitProcessing || state.entries.entriesInTransit;

  const editingEntry = get(state, "entry", {});
  const hasMore = get(state, "entries.hasMore", false);

  return {
    editingEntry,
    firstEntryDate: get(ownProps, "entries[0].entryDate"),
    hasMore,
    historyContext,
    loading,
  };
}

export default connect(mapStateToProps)(DayEntry);

function HistoryDate({ firstEntryDate }) {
  const date = useMemo(() => firstEntryDate.format("L"), [firstEntryDate]);
  const day = useMemo(() => firstEntryDate.format("ddd").toUpperCase(), [firstEntryDate]);

  return <h5 className="history-date">{day}<br />{date}</h5>;
}



// WEBPACK FOOTER //
// ./app/components/History/DayEntry.jsx